import React from 'react';
import {
    Select,
    MenuItem,
    IconButton,
    SelectProps,
    Grid,
    PaperProps,
    CircularProgress,
    Box,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import theme from '../../../theme/Theme';
import ChevronDown from "../../../../public/assets/icons/chevron-down.svg";
import { OptionEntry } from '../../../types/cqa';

interface CustomDropdownProps extends Omit<SelectProps, 'onChange'> {
    options: string[] | OptionEntry[]; // List of options for the dropdown
    selectedValue: string | null; // Currently selected value
    onChange: (value: string | OptionEntry) => void; // Change handler for selected value
    placeholder?: string; // Placeholder text
    dropdownStyle?: PaperProps['sx']; // Optional style for dropdown
    listItemStyle?: SxProps<Theme>;
    paperPropsStyle?: SxProps<Theme>;
    menuListStyle?: SxProps<Theme>;
    icon?: React.ReactNode; // Optional custom icon
    loading : boolean
}

const defaultStyle = {
    height: "37px",
    width: "100%",
    paddingRight: "8px",
    borderRadius: "3px",
    "& .MuiSelect-select": {
      paddingRight: "16px !important",
    },
    "& .MuiSelect-icon": {
      color: theme.palette.artisticTextInteractive.main,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.artisticStroke200.main} !important`,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.artisticGrey400.main} !important`,
    },
  };

const Dropdown: React.FC<CustomDropdownProps> = ({
    options,
    selectedValue,
    onChange,
    placeholder = 'Please select...',
    dropdownStyle,
    paperPropsStyle,
    listItemStyle,
    menuListStyle,
    loading,
    ...props
}) => {
    const theme: Theme = useTheme();

    const _dropdownStyle = {...defaultStyle, ...dropdownStyle}

    return (        
            <Select
                data-testid="dropDownSingleSelect"
                variant="outlined"
                value={selectedValue || ''}
                onChange={(e) => onChange(e.target.value as string)}
                displayEmpty
                renderValue={(selected) => {
                    if (!selectedValue) {
                        return (
                            <div
                                style={{ color: theme.palette.artisticTextLow.main }}
                            >
                                {placeholder}
                            </div>
                        );
                    }

                    return <Box sx={listItemStyle}>{selectedValue}</Box>;
                }}
                IconComponent={() => (
                    <>
                      {loading ? (
                        <CircularProgress color="primary" size={20} />
                      ) : (
                        <IconButton disableRipple>
                          <img src={ChevronDown} alt="chevron-down" />
                        </IconButton>
                      )}
                    </>
                  )}
                sx={_dropdownStyle}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            "& .MuiList-root": {
                                width: "100%",
                            },
                            ...paperPropsStyle
                        },
                    },
                    MenuListProps: {
                      sx: {
                        ...menuListStyle
                      },
                    }
                }}
                {...props}
            >
                {options.map((option) => (
                    <MenuItem key={(option instanceof Object) ? option.value : option} value={option} sx={{ padding: '8px 16px' }}>
                        {
                          (option instanceof Object) ? option.label : option
                        }
                    </MenuItem>
                ))}
            </Select>        
    );
};

export default Dropdown;
